import { useMemo, useState } from 'react'
import { Card, PageHeader, Pill, SectionLabel, type PillTone } from '../components/ui'
import { useTeam } from '../hooks/useTeam'
import {
  CAPABILITIES,
  CAPABILITY_LABELS,
  ROLES,
  ROLE_LABELS,
  canAssignRole,
  canEditCapability,
  groupRoster,
  locationScopeLabel,
  roleTone,
  statusMeta,
  type Capability,
  type Role,
  type TeamMember,
  type Tone,
} from '../lib/teamView'

// Team management (Screen: /settings/team). Owner/admin only — the backend is
// authoritative (every mutation re-checks); this page reads /team/me to hide the
// UI and handles 403 safely if reached anyway.

const toneToPill = (t: Tone): PillTone =>
  t === 'success' ? 'accent' : t === 'warning' ? 'warning' : t === 'danger' ? 'danger' : 'neutral'

const initialsOf = (name: string) =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '—'

export default function TeamManagement() {
  const { access, members, locations, loading, error, invite, updateMember, setMemberLocations } = useTeam()
  const [banner, setBanner] = useState<{ msg: string; tone: 'ok' | 'err' } | null>(null)
  const isOwner = access?.is_owner === true

  const flash = (msg: string, tone: 'ok' | 'err') => {
    setBanner({ msg, tone })
    setTimeout(() => setBanner(null), 4500)
  }

  const groups = useMemo(() => groupRoster(members), [members])
  const totalLoc = locations.length

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <PageHeader title="Team" subtitle="Loading team…" />
      </div>
    )
  }

  // Backend-authoritative guard: no manage_team → honest access panel, no roster.
  if (!access?.can_manage_team) {
    return (
      <div style={{ padding: 24 }}>
        <PageHeader title="Team" />
        <Card style={{ padding: 28, maxWidth: 560 }}>
          <SectionLabel>Access</SectionLabel>
          <p style={{ color: 'var(--text-2)', marginTop: 8, lineHeight: 1.6 }}>
            Team management is limited to organization owners and admins. If you need to invite or manage
            teammates, ask an owner to grant you admin access.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        title="Team"
        subtitle={`${groups.active.length} active · ${groups.pending.length} pending · ${groups.deactivated.length} deactivated`}
      />

      {banner && (
        <Card
          style={{
            padding: '12px 16px',
            borderLeft: `3px solid ${banner.tone === 'ok' ? 'var(--accent)' : 'var(--danger)'}`,
            color: banner.tone === 'ok' ? 'var(--text-1)' : 'var(--danger)',
            fontSize: 14,
          }}
        >
          {banner.msg}
        </Card>
      )}

      <InviteForm
        locations={locations}
        isOwner={isOwner}
        onInvite={async (payload) => {
          const r = await invite(payload)
          flash(r.ok ? `Invitation sent to ${payload.email}.` : r.error!, r.ok ? 'ok' : 'err')
          return r.ok
        }}
      />

      <RosterSection title="Pending invitations" empty="No pending invitations." members={groups.pending}
        {...{ locations, isOwner, totalLoc, updateMember, setMemberLocations, flash }} />
      <RosterSection title="Active members" empty="No active members yet." members={groups.active}
        {...{ locations, isOwner, totalLoc, updateMember, setMemberLocations, flash }} />
      <RosterSection title="Deactivated" empty="No deactivated members." members={groups.deactivated}
        {...{ locations, isOwner, totalLoc, updateMember, setMemberLocations, flash }} />

      {error && <Card style={{ padding: 16, color: 'var(--danger)' }}>{error}</Card>}
    </div>
  )
}

// ── Invite form ──────────────────────────────────────────────────────────────
function InviteForm({
  locations,
  isOwner,
  onInvite,
}: {
  locations: { id: string; name: string }[]
  isOwner: boolean
  onInvite: (p: {
    email: string
    role: Role
    all_locations: boolean
    location_ids: string[]
    permissions: Partial<Record<Capability, boolean>>
  }) => Promise<boolean>
}) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('manager')
  const [allLoc, setAllLoc] = useState(false)
  const [locIds, setLocIds] = useState<string[]>([])
  const [perms, setPerms] = useState<Partial<Record<Capability, boolean>>>({})
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!email.trim()) return
    setBusy(true)
    const ok = await onInvite({ email: email.trim(), role, all_locations: allLoc, location_ids: allLoc ? [] : locIds, permissions: perms })
    setBusy(false)
    if (ok) {
      setEmail('')
      setRole('manager')
      setAllLoc(false)
      setLocIds([])
      setPerms({})
    }
  }

  return (
    <Card style={{ padding: 20 }}>
      <SectionLabel>Invite a teammate</SectionLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12, alignItems: 'flex-end' }}>
        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@company.com"
            style={inputStyle}
          />
        </Field>
        <Field label="Role">
          <select value={role} onChange={(e) => setRole(e.target.value as Role)} style={inputStyle}>
            {ROLES.filter((r) => canAssignRole(isOwner, r)).map((r) => (
              <option key={r} value={r}>{ROLE_LABELS[r]}</option>
            ))}
          </select>
        </Field>
      </div>

      <div style={{ marginTop: 16 }}>
        <SectionLabel>Location access</SectionLabel>
        <label style={checkRow}>
          <input type="checkbox" checked={allLoc} onChange={(e) => setAllLoc(e.target.checked)} />
          <span>Access to all locations (organization-wide)</span>
        </label>
        {!allLoc && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {locations.length === 0 && <span style={{ color: 'var(--text-3)', fontSize: 13 }}>No locations yet.</span>}
            {locations.map((l) => {
              const on = locIds.includes(l.id)
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setLocIds((p) => (on ? p.filter((x) => x !== l.id) : [...p, l.id]))}
                  style={{ ...chipStyle, ...(on ? chipOn : {}) }}
                >
                  {l.name}
                </button>
              )
            })}
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <SectionLabel>Capabilities</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 6, marginTop: 8 }}>
          {CAPABILITIES.map((cap) => {
            const editable = canEditCapability(isOwner, cap)
            return (
              <label key={cap} style={{ ...checkRow, opacity: editable ? 1 : 0.5 }} title={editable ? '' : 'Owner-only — an admin cannot change this.'}>
                <input
                  type="checkbox"
                  disabled={!editable}
                  checked={!!perms[cap]}
                  onChange={(e) => setPerms((p) => ({ ...p, [cap]: e.target.checked }))}
                />
                <span>{CAPABILITY_LABELS[cap]}{!editable && ' 🔒'}</span>
              </label>
            )
          })}
        </div>
        {!isOwner && (
          <p style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 6 }}>
            Billing, admin, and team-management permissions can only be changed by an owner.
          </p>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <button type="button" onClick={submit} disabled={busy || !email.trim()} style={primaryBtn}>
          {busy ? 'Sending…' : 'Send invitation'}
        </button>
      </div>
    </Card>
  )
}

// ── Roster section + rows ────────────────────────────────────────────────────
function RosterSection({
  title,
  empty,
  members,
  locations,
  isOwner,
  totalLoc,
  updateMember,
  setMemberLocations,
  flash,
}: {
  title: string
  empty: string
  members: TeamMember[]
  locations: { id: string; name: string }[]
  isOwner: boolean
  totalLoc: number
  updateMember: ReturnType<typeof useTeam>['updateMember']
  setMemberLocations: ReturnType<typeof useTeam>['setMemberLocations']
  flash: (msg: string, tone: 'ok' | 'err') => void
}) {
  return (
    <div>
      <SectionLabel>{title}</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
        {members.length === 0 && <Card style={{ padding: 16, color: 'var(--text-3)', fontSize: 13 }}>{empty}</Card>}
        {members.map((m) => (
          <MemberRow key={m.id} m={m} {...{ locations, isOwner, totalLoc, updateMember, setMemberLocations, flash }} />
        ))}
      </div>
    </div>
  )
}

function MemberRow({
  m,
  locations,
  isOwner,
  totalLoc,
  updateMember,
  setMemberLocations,
  flash,
}: {
  m: TeamMember
  locations: { id: string; name: string }[]
  isOwner: boolean
  totalLoc: number
  updateMember: ReturnType<typeof useTeam>['updateMember']
  setMemberLocations: ReturnType<typeof useTeam>['setMemberLocations']
  flash: (msg: string, tone: 'ok' | 'err') => void
}) {
  const [open, setOpen] = useState(false)
  const st = statusMeta(m.status)
  // An admin cannot modify an owner member (mirrors backend).
  const locked = m.role === 'owner' && !isOwner

  const doUpdate = async (patch: Parameters<typeof updateMember>[1], okMsg: string) => {
    const r = await updateMember(m.id, patch)
    flash(r.ok ? okMsg : r.error!, r.ok ? 'ok' : 'err')
  }

  return (
    <Card style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={avatar}>{initialsOf(m.name)}</div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{m.name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>{m.email || m.invited_email || '—'}</div>
        </div>
        <Pill tone={toneToPill(st.tone)}>{st.label}</Pill>
        <Pill tone={toneToPill(roleTone(m.role))}>{ROLE_LABELS[m.role]}</Pill>
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{locationScopeLabel(m, totalLoc)}</span>
        {!locked && (
          <button type="button" onClick={() => setOpen((o) => !o)} style={ghostBtn}>
            {open ? 'Close' : 'Manage'}
          </button>
        )}
        {locked && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Owner — admins can’t modify</span>}
      </div>

      {open && !locked && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--surface-3)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Role */}
          <Field label="Role">
            <select
              defaultValue={m.role}
              onChange={(e) => doUpdate({ role: e.target.value as Role }, 'Role updated.')}
              style={inputStyle}
            >
              {ROLES.filter((r) => canAssignRole(isOwner, r) || r === m.role).map((r) => (
                <option key={r} value={r} disabled={!canAssignRole(isOwner, r)}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </Field>

          {/* All locations */}
          <label style={checkRow}>
            <input
              type="checkbox"
              defaultChecked={m.all_locations}
              onChange={(e) => doUpdate({ all_locations: e.target.checked }, 'Location scope updated.')}
            />
            <span>Access to all locations</span>
          </label>

          {/* Explicit locations */}
          {!m.all_locations && (
            <div>
              <SectionLabel>Explicit locations</SectionLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {locations.map((l) => {
                  const on = m.location_ids.includes(l.id)
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={async () => {
                        const next = on ? m.location_ids.filter((x) => x !== l.id) : [...m.location_ids, l.id]
                        const r = await setMemberLocations(m.id, next)
                        flash(r.ok ? 'Locations updated.' : r.error!, r.ok ? 'ok' : 'err')
                      }}
                      style={{ ...chipStyle, ...(on ? chipOn : {}) }}
                    >
                      {l.name}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Capabilities */}
          <div>
            <SectionLabel>Capabilities</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 6, marginTop: 8 }}>
              {CAPABILITIES.map((cap) => {
                const editable = canEditCapability(isOwner, cap)
                return (
                  <label key={cap} style={{ ...checkRow, opacity: editable ? 1 : 0.5 }} title={editable ? '' : 'Owner-only — an admin cannot change this.'}>
                    <input
                      type="checkbox"
                      disabled={!editable}
                      defaultChecked={!!m.permission_overrides[cap]}
                      onChange={(e) =>
                        doUpdate({ permissions: { ...m.permission_overrides, [cap]: e.target.checked } }, 'Permissions updated.')
                      }
                    />
                    <span>{CAPABILITY_LABELS[cap]}{!editable && ' 🔒'}</span>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Activate / deactivate */}
          <div>
            {m.status === 'deactivated' ? (
              <button type="button" style={primaryBtn} onClick={() => doUpdate({ active: true }, `${m.name} reactivated.`)}>
                Reactivate member
              </button>
            ) : (
              <button
                type="button"
                style={dangerBtn}
                onClick={() => {
                  if (window.confirm(`Deactivate ${m.name}? They will immediately lose access. Their role and location settings are kept, and you can reactivate them later.`)) {
                    doUpdate({ active: false }, `${m.name} deactivated.`)
                  }
                }}
              >
                Deactivate member
              </button>
            )}
          </div>
        </div>
      )}
    </Card>
  )
}

// ── small presentational bits ────────────────────────────────────────────────
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-2)' }}>
      <span>{label}</span>
      {children}
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  background: 'var(--surface-2)',
  border: '1px solid var(--surface-3)',
  borderRadius: 8,
  padding: '8px 10px',
  color: 'var(--text-1)',
  fontSize: 14,
  minWidth: 200,
}
const checkRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-2)', cursor: 'pointer', padding: '3px 0' }
const chipStyle: React.CSSProperties = { background: 'var(--surface-2)', border: '1px solid var(--surface-3)', borderRadius: 999, padding: '5px 12px', fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' }
const chipOn: React.CSSProperties = { background: 'var(--accent-soft)', borderColor: 'var(--accent)', color: 'var(--accent)' }
const primaryBtn: React.CSSProperties = { background: 'var(--accent)', color: '#0b0f12', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const dangerBtn: React.CSSProperties = { background: 'transparent', color: 'var(--danger)', border: '1px solid var(--danger)', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const ghostBtn: React.CSSProperties = { background: 'transparent', color: 'var(--text-2)', border: '1px solid var(--surface-3)', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }
const avatar: React.CSSProperties = { width: 36, height: 36, borderRadius: 999, background: 'var(--surface-3)', color: 'var(--text-1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, flexShrink: 0 }
